import { Dropdown } from "primereact/dropdown";
import FormSubHeading from "../../components/FormSubHeading";
import InputWithSuggestions from "../../components/InputWithSuggestions";
import { getPeopleMatchingCI } from "../../services/people";
import { useRef, useState } from "react";
import { createSearchItemTemplate } from "../../utils";
import { InputText } from "primereact/inputtext";
import FieldInfo from "../../components/FieldInfo";
import { SelectButton } from "primereact/selectbutton";
import { GENDERS } from "../../constants";
import VSieteDateInput from "../../components/VSieteDateInput";
import { useStore } from "@tanstack/react-store";
import { useDependencyInsured } from "../../hooks/queries";

export default function PatientData({
  form,
  holderQuery,
  onHolderSearchEnter,
  selectHolder,
  dependency,
  isRmsActive,
}) {
  // Búsqueda de pacientes
  const [patientResult, setPatientResult] = useState([]);
  const patientSearchRef = useRef(null);

  // Pacientes obtenidos por póliza RMS
  const [patientSelected, setPatientSelected] = useState(null);
  const [patientSearchRmsLoading, setPatientSearchRmsLoading] = useState(false);

  // Extraer estado del formulario
  const patientCiType = useStore(
    form.store,
    (state) => state.values.patient.ciType
  );
  const holderCiType = useStore(form.store, (state) => state.values.ciType);
  const holderCi = useStore(form.store, (state) => state.values.ci);
  const subsidiaryPatient = useStore(
    form.store,
    (state) => state.values.patient.subsidiary
  );

  const dependencyInsured = useDependencyInsured(
    holderCiType,
    holderCi,
    subsidiaryPatient,
    {
      enabled:
        !!holderCiType && !!holderCi && !!subsidiaryPatient && isRmsActive,
      select: (data) => {
        return [
          ...data.results
            .filter((item) => !item.descripcion.includes("Titular"))
            .map((item) => ({
              ...item,
              label: `${item.cd_nac_aseg}${item.cedrif_aseg}${
                item.correl_aseg > 0 ? `-${item.correl_aseg}` : ""
              } - ${item.nombre}`,
              value: item.serialaseg,
            })),
          {
            cd_nac_aseg: "V",
            cedrif_aseg: "",
            correl_aseg: 0,
            nombre: "",
            descripcion: "Otro",
            serialaseg: -1,
            label: "OTRO",
            value: -1,
          },
        ];
      },
    }
  );

  const searchPatientDependencyInsured = async (patient) => {
    if (!patient) return;
    setPatientSearchRmsLoading(true);
    let ciForSearch = `${patient?.cd_nac_aseg}${patient?.cedrif_aseg || ""}`;
    if (patient.correl_aseg) {
      ciForSearch += `-${patient.correl_aseg}`;
    }
    const response = await getPeopleMatchingCI(ciForSearch);
    setPatientSearchRmsLoading(false);
    if (response.length > 0) {
      const foundPatient = response[0];
      form.setFieldValue("patient.name", foundPatient.name);
      form.setFieldValue("patient.sex", foundPatient.sex);
      form.setFieldValue(
        "patient.birthDate",
        foundPatient.birthdate.split("-").reverse().join("/")
      );
    }
  };

  const searchPatient = async (e) => {
    if (e.query && /\D/.test(e.query)) {
      form.setFieldValue("patient.ci", e.query.replace(/\D/g, ""));
      e.query = e.query.replace(/\D/g, "");
      searchPatient(e);
      return;
    }
    const response = await getPeopleMatchingCI(patientCiType + e.query);
    const changeHolder = response.map((item) => ({
      ...item,
      label: `${item.ci} - ${item.name}`,
      value: item.ci.replace(/\D/g, ""),
    }));
    setPatientResult(changeHolder);
  };

  return (
    <div>
      <FormSubHeading title="Datos del Paciente" />
      <div className="p-field">
        <label htmlFor="patientCi">Cédula</label>
        {isRmsActive && (
          <div className="mb-2">
            <Dropdown
              options={dependencyInsured.data || []}
              placeholder="Seleccionar paciente"
              loading={dependencyInsured.isLoading}
              disabled={dependencyInsured.isLoading}
              value={patientSelected}
              onChange={(e) => {
                setPatientSelected(e.value);
                form.setFieldValue("patient.sex", null);
                form.setFieldValue("patient.birthDate", "");
                if (e.value === -1) {
                  form.setFieldValue("patient.ciType", "V");
                  form.setFieldValue("patient.ci", "");
                  form.setFieldValue("patient.name", "");
                  form.setFieldValue("patient.isMinor", false);
                  form.setFieldValue("patient.minorId", "");
                  return;
                }
                const patient = dependencyInsured.data.find(
                  (item) => item.serialaseg === e.value
                );
                if (patient) {
                  searchPatientDependencyInsured(patient);
                  form.setFieldValue("patient.ciType", patient.cd_nac_aseg);
                  form.setFieldValue("patient.ci", patient.cedrif_aseg);
                  form.setFieldValue("patient.name", patient.nombre);
                  if (patient.correl_aseg) {
                    form.setFieldValue("patient.isMinor", true);
                    form.setFieldValue("patient.minorId", patient.correl_aseg);
                  } else {
                    form.setFieldValue("patient.isMinor", false);
                    form.setFieldValue("patient.minorId", "");
                  }
                }
              }}
            />
          </div>
        )}
        {(!isRmsActive || patientSelected === -1) && (
          <div className="field-with-select">
            <form.Field name="patient.ciType">
              {(field) => (
                <Dropdown
                  id={"patient" + field.name}
                  name={"patient" + field.name}
                  options={[
                    { label: "V", value: "V" },
                    { label: "E", value: "E" },
                    { label: "MENOR", value: "M" },
                  ]}
                  placeholder="Tipo"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.value)}
                  required
                />
              )}
            </form.Field>
            {patientCiType !== "M" && (
              <form.Field name="patient.ci">
                {(field) => (
                  <InputWithSuggestions
                    inputName={field.name}
                    inputValue={field.state.value}
                    keyfilter="pint"
                    maxLength={9}
                    onCompleteMethod={searchPatient}
                    suggestions={patientResult}
                    onTextChange={(e) => {
                      if (e.value && e.value.hasOwnProperty("value")) {
                        field.handleChange(e.value.value);
                        form.setFieldValue("patient.name", e.value.name);
                        form.setFieldValue("patient.sex", e.value.sex);
                        form.setFieldValue(
                          "patient.birthDate",
                          e.value.birthdate.split("-").reverse().join("/")
                        );
                        form.setFieldValue(
                          "patient.condition",
                          e.value.condition
                        );
                        // form.setFieldValue("phoneNumber", e.value)
                        const phone = e.value.phone;
                        form.setFieldValue(
                          "patient.phoneCode",
                          phone.slice(0, 5)
                        );
                        form.setFieldValue(
                          "patient.phoneNumber",
                          phone.slice(5)
                        );
                        // phone
                        form.validateSync();

                        return;
                      }
                      field.handleChange(e.value);
                    }}
                    query={holderQuery}
                    notFoundBtnLabel="Crear registro de asegurado"
                    notFoundBtnClick={() => {}}
                    itemTemplate={createSearchItemTemplate(selectHolder)}
                    overlayRef={patientSearchRef}
                    onSearchEnter={onHolderSearchEnter}
                  />
                )}
              </form.Field>
            )}
            {patientCiType === "M" && (
              <form.Field name="patient.minorId">
                {(field) => (
                  <Dropdown
                    id="minorId"
                    name="minorId"
                    options={dependency}
                    placeholder="Dependencia"
                    optionLabel="name"
                    optionValue="code"
                    value={field.state.value}
                    onChange={(e) => {
                      field.handleChange(e.value);
                      const dependencySelect = dependency.find(
                        (item) => item.code === e.value
                      );
                      if (dependencySelect) {
                        form.setFieldValue(
                          "patient.name",
                          dependencySelect.name
                        );
                      }
                    }}
                  />
                )}
              </form.Field>
            )}
          </div>
        )}
      </div>

      <form.Field
        name="patient.name"
        children={(field) => (
          <div className="p-field mt-2">
            <label htmlFor="patientName">Nombre completo</label>
            <InputText
              id="patientName"
              name="patientName"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              invalid={field.state.meta.errors.length > 0}
              ref={patientSearchRef}
              loading={patientSearchRmsLoading}
              disabled={patientSearchRmsLoading}
            />
            <FieldInfo field={field} />
          </div>
        )}
      />

      <form.Field
        name="patient.sex"
        children={(field) => (
          <div className="p-field">
            <label htmlFor="patientSex">Sexo</label>
            <SelectButton
              id="patientSex"
              name="patientSex"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              options={GENDERS}
              invalid={field.state.meta.errors.length > 0}
              loading={patientSearchRmsLoading}
              disabled={patientSearchRmsLoading}
            />
            <FieldInfo field={field} />
          </div>
        )}
      />

      <form.Field name="patient.birthDate">
        {(field) => (
          <div className="p-field">
            <span>Fecha de nacimiento</span>
            <VSieteDateInput
              name="patientBirthdate"
              value={field.state.value}
              setValue={(value) => field.handleChange(value)}
              invalid={field.state.meta.errors.length > 0}
              disabled={patientSearchRmsLoading}
            />
          </div>
        )}
      </form.Field>
    </div>
  );
}
